/**
 * I18n Loader для Kernel
 * Адаптований для роботи в SPA та Framework режимах з підтримкою lazy loading
 */
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import { IConfigLoader } from '../../Contracts/Loaders/IConfigLoader'
import { BaseLoader } from './BaseLoader'

export interface I18nResources {
    [language: string]: {
        [namespace: string]: Record<string, any>
    }
}

export class I18nKernelLoader extends BaseLoader {
    private resources: I18nResources = {}
    private namespaces: string[] = []
    private localeCache: Map<string, Record<string, any>> = new Map()
    private loadedLanguages: Set<string> = new Set()

    constructor() {
        const loaderConfig: IConfigLoader = {
            name: 'i18n',
            priority: 12,
            clientSafe: true,
            dependencies: ['config'],
        }
        super(loaderConfig)
    }

    /**
     * Нормалізація мовного коду для відповідності структурі папок локалізації
     * uk_UA -> uk, en_US -> en, en_GB -> en
     */
    private normalizeLanguageCode(langCode: string): string {
        // Якщо код містить underscore або dash, беремо першу частину
        return langCode.split(/[_-]/)[0]
    }

    async load(): Promise<{ i18n: any; resources: I18nResources; namespaces: string[] }> {
        try {
            this.log('Starting i18n loading...')

            // Динамічне завантаження файлів локалізації
            // Core локалізація через модуль @/Core
            const coreFiles = import.meta.glob('@/Core/Locales/*/*.json', { eager: true })
            // Ship та Containers локалізація відносно кореня проекту
            const shipFiles = import.meta.glob('/src/Ship/Locales/*/*.json', { eager: true })
            const containersFiles = import.meta.glob('/src/Containers/*Section/*Container/Locales/*/*.json', {
                eager: true,
            })

            this.resources = {}
            this.namespaces = []

            // Завантаження Core локалізації
            if (Object.keys(coreFiles).length > 0) {
                this.namespaces.push('Core')
                this.processFiles(coreFiles, /\/Locales\/(\w+)\/(.+)\.json$/, 'Core')
            }

            // Завантаження Ship локалізації
            if (Object.keys(shipFiles).length > 0) {
                this.namespaces.push('Ship')
                this.processFiles(shipFiles, /\/Ship\/Locales\/(\w+)\/(.+)\.json$/, 'Ship')
            }

            // Завантаження Containers локалізації
            if (Object.keys(containersFiles).length > 0) {
                this.processContainerFiles(containersFiles)
            }

            // Отримуємо мову з .env та нормалізуємо її
            const defaultLocale = import.meta.env.VITE_DEFAULT_LOCALE || 'uk_UA'
            const normalizedLng = this.normalizeLanguageCode(defaultLocale)

            // Ініціалізація i18next з підтримкою pluralization
            await i18n.use(initReactI18next).init({
                resources: this.resources,
                ns: this.namespaces,
                fallbackLng: 'en',
                lng: normalizedLng, // Мова з .env (нормалізована до формату папок)
                defaultNS: this.namespaces.includes('Ship') ? 'Ship' : 'Core',
                debug: false,
                interpolation: {
                    escapeValue: false,
                },
                pluralSeparator: '_',
                contextSeparator: '_',
                parseMissingKeyHandler: (missingKey: string) => {
                    return missingKey.substring(missingKey.lastIndexOf(':') + 1)
                },
            })

            // Відстежуємо завантажені мови
            Object.keys(this.resources).forEach((lang) => this.loadedLanguages.add(lang))

            this.log(`Loaded ${this.loadedLanguages.size} languages with ${this.namespaces.length} namespaces`)

            return {
                i18n,
                resources: this.resources,
                namespaces: this.namespaces,
            }
        } catch (error) {
            this.log('Error loading i18n, using fallback: ' + error, 'error')
            console.warn('⚠️ Помилка завантаження інтернаціоналізації, використовується fallback:', error)
            return this.getFallbackI18n()
        }
    }

    /**
     * Lazy loading для конкретної мови
     */
    async loadLanguageLazy(language: string): Promise<void> {
        if (this.loadedLanguages.has(language)) {
            this.log(`Language ${language} already loaded`)
            return
        }

        this.log(`Lazy loading language: ${language}`)

        try {
            // Тут можна додати lazy імпорт конкретних файлів мови
            // const coreFiles = await import(`@/Core/Locales/${language}/*.json`)
            // Наразі використовуємо існуючі ресурси
            
            this.loadedLanguages.add(language)
            this.log(`Successfully loaded language: ${language}`)
        } catch (error) {
            this.log(`Error loading language ${language}: ${error}`, 'error')
        }
    }

    /**
     * Lazy loading для конкретного namespace
     */
    async loadNamespace(namespace: string): Promise<void> {
        if (!i18n.hasResourceBundle(i18n.language, namespace)) {
            this.log(`Loading namespace: ${namespace}`)
            
            try {
                const resources = this.resources[i18n.language]?.[namespace]
                if (resources) {
                    i18n.addResourceBundle(i18n.language, namespace, resources, true, true)
                    this.log(`Successfully loaded namespace: ${namespace}`)
                }
            } catch (error) {
                this.log(`Error loading namespace ${namespace}: ${error}`, 'error')
            }
        }
    }

    private getFallbackI18n(): { i18n: any; resources: I18nResources; namespaces: string[] } {
        return {
            i18n: null,
            resources: {
                uk: {
                    common: {
                        loading: 'Завантаження...',
                        error: 'Помилка',
                        save: 'Зберегти',
                        cancel: 'Скасувати',
                    },
                },
                en: {
                    common: {
                        loading: 'Loading...',
                        error: 'Error',
                        save: 'Save',
                        cancel: 'Cancel',
                    },
                },
            },
            namespaces: ['common'],
        }
    }

    /**
     * Обробка файлів локалізації з кешуванням
     */
    private processFiles(files: Record<string, any>, pattern: RegExp, namespace: string): void {
        Object.entries(files).forEach(([key, value]) => {
            const cacheKey = `${namespace}:${key}`
            
            // Перевіряємо кеш
            if (this.localeCache.has(cacheKey)) {
                return
            }

            const match = key.match(pattern)

            if (match) {
                const lang = match[1]
                const ns = namespace

                if (!lang) return

                if (!this.resources[lang]) {
                    this.resources[lang] = {}
                }

                const translationData = (value as any).default
                this.resources[lang][ns] = {
                    ...this.resources[lang][ns],
                    ...translationData,
                }

                // Зберігаємо в кеш
                this.localeCache.set(cacheKey, translationData)
            }
        })
    }

    /**
     * Обробка файлів локалізації контейнерів
     */
    private processContainerFiles(files: Record<string, any>): void {
        Object.entries(files).forEach(([key, value]) => {
            const match = key.match(/\/Containers\/(.+)Section\/(.+)Container\/Locales\/(\w+)\/(.+)\.json$/)

            if (match) {
                const sectionName = match[1]
                const containerName = match[2]
                const lang = match[3]
                const ns = `${sectionName}.${containerName}`

                if (!lang) return

                if (!this.resources[lang]) {
                    this.resources[lang] = {}
                }

                if (!this.resources[lang][ns]) {
                    this.resources[lang][ns] = {}
                    this.namespaces.push(ns)
                }

                this.resources[lang][ns] = {
                    ...this.resources[lang][ns],
                    ...(value as any).default,
                }
            }
        })
    }

    /**
     * Отримати всі ресурси локалізації
     */
    getResources(): I18nResources {
        return this.resources
    }

    /**
     * Отримати ресурси для конкретної мови
     */
    getResourcesForLanguage(language: string): any {
        return this.resources[language] || {}
    }

    /**
     * Отримати всі доступні мови
     */
    getAvailableLanguages(): string[] {
        return Object.keys(this.resources)
    }

    /**
     * Отримати завантажені мови
     */
    getLoadedLanguages(): string[] {
        return Array.from(this.loadedLanguages)
    }

    /**
     * Отримати всі namespaces
     */
    getNamespaces(): string[] {
        return this.namespaces
    }

    /**
     * Отримати статистику кешу
     */
    getCacheStatistics(): { size: number; keys: string[] } {
        return {
            size: this.localeCache.size,
            keys: Array.from(this.localeCache.keys()),
        }
    }

    /**
     * Очистити кеш локалізації
     */
    clearLocaleCache(): void {
        this.localeCache.clear()
        this.log('Locale cache cleared')
    }
}
